import type { PVSizingWarning } from "../services/pvSizing";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = PVSizingWarning["code"];

type WarningGroup = "no-solution" | "not-established" | "not-evaluated" | "other";

interface PVWarningPanelProps {
  warnings: PVSizingWarning[];
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error. A
// PV warning carries only a code and a message - there is no severity and no
// reference field on this contract, so none is shown.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  DERATING_APPLIED: "Derating applied",
  PV_ENERGY_YIELD_NOT_EVALUATED: "Annual energy yield not evaluated",
  PV_BATTERY_NOT_EVALUATED: "Battery or off-grid arrangement not evaluated",
  EXPORT_LIMIT_APPLIED: "Export limit applied",
  DG_COORDINATION_REQUIRED: "DG coordination required",
  NO_STANDARD_INVERTER_RATING: "No standard inverter rating fits",
  STRING_LENGTH_WINDOW_EMPTY: "No string length fits the MPPT window",
  MPPT_INPUT_LIMIT_EXCEEDED: "MPPT input limit exceeded",
  GROWTH_FACTOR_NOT_ESTABLISHED: "Future growth factor not established",
  DESIGN_MARGIN_NOT_ESTABLISHED: "Design margin factor not established",
  AMBIENT_DERATING_NOT_ESTABLISHED: "Ambient derating factor not established",
  ALTITUDE_DERATING_NOT_ESTABLISHED: "Altitude derating factor not established",
  AC_EFFICIENCY_NOT_ESTABLISHED: "AC efficiency factor not established",
};

// Where each code is listed. Why there is nothing to build comes first (A19 (l)),
// then an input the engineer still owes (it is why the result needs review), then
// what the method does not evaluate (A19 (b), GAP-019), then everything else.
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  STRING_LENGTH_WINDOW_EMPTY: "no-solution",
  MPPT_INPUT_LIMIT_EXCEEDED: "no-solution",
  NO_STANDARD_INVERTER_RATING: "no-solution",
  GROWTH_FACTOR_NOT_ESTABLISHED: "not-established",
  DESIGN_MARGIN_NOT_ESTABLISHED: "not-established",
  AMBIENT_DERATING_NOT_ESTABLISHED: "not-established",
  ALTITUDE_DERATING_NOT_ESTABLISHED: "not-established",
  AC_EFFICIENCY_NOT_ESTABLISHED: "not-established",
  PV_ENERGY_YIELD_NOT_EVALUATED: "not-evaluated",
  PV_BATTERY_NOT_EVALUATED: "not-evaluated",
  DERATING_APPLIED: "other",
  EXPORT_LIMIT_APPLIED: "other",
  DG_COORDINATION_REQUIRED: "other",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "no-solution", title: "No solution" },
  { group: "not-established", title: "Not established (review required)" },
  { group: "not-evaluated", title: "Not evaluated" },
  { group: "other", title: "Other warnings" },
];

export function PVWarningPanel({ warnings }: PVWarningPanelProps) {
  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            // Within a group the engine's own order is kept.
            const members = warnings.filter(
              (warning) => WARNING_CODE_GROUPS[warning.code] === group,
            );
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning, index) => (
                    <li key={`${warning.code}-${index}`} data-warning-code={warning.code}>
                      <strong>{WARNING_CODE_LABELS[warning.code]}</strong>
                      {": "}
                      {/* The engine's own sentence, kept as it was sent. */}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default PVWarningPanel;
